import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// 导入通用组件
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/UI/LoadingSpinner';

// 导入上下文
import { LanguageContext } from './context/LanguageContext';

// 使用代码分割懒加载页面组件
const HomePage = lazy(() => import('./pages/HomePage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// 检测浏览器首选语言并返回支持的语言代码
const detectBrowserLanguage = () => {
  // 支持的语言列表
  const supportedLanguages = ['zh-CN', 'en-US'];
  
  // 尝试从localStorage读取之前保存的语言设置
  const savedLanguage = localStorage.getItem('preferredLanguage');
  if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
    return savedLanguage;
  }
  
  // 获取浏览器语言
  const browserLang = navigator.language || navigator.userLanguage;
  console.log('检测到浏览器语言:', browserLang);
  
  // 检查浏览器语言是否为中文
  if (browserLang.startsWith('zh')) {
    return 'zh-CN';
  }
  
  // 默认返回英文
  return 'en-US';
};

function App() {
  // 设置语言，自动检测浏览器首选语言
  const [language, setLanguage] = useState(detectBrowserLanguage());
  const location = useLocation();
  const pathname = location.pathname;
  
  // 当语言变化时，保存到localStorage
  useEffect(() => {
    localStorage.setItem('preferredLanguage', language);
    document.documentElement.lang = language;
  }, [language]);
  
  // 自定义setLanguage函数，同时保存用户选择
  const handleSetLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  // 网站基础URL
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://yourgamesite.com';

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage }}>
      <div className="flex flex-col min-h-screen">
        <Helmet>
          <title>
            {language === 'zh-CN' 
              ? '顶尖小游戏 - 海量精彩游戏等你体验' 
              : 'Top Mini Games - Enjoy a Variety of Exciting Games'}
          </title>
          <meta 
            name="description" 
            content={language === 'zh-CN'
              ? '多合一游戏平台，提供各类休闲、动作、益智、体育游戏，一站式畅玩体验。'
              : 'An all-in-one gaming platform offering various casual, action, puzzle, and sports games for a one-stop gaming experience.'
            }
          />
          <meta 
            name="keywords" 
            content={language === 'zh-CN'
              ? '顶尖小游戏,游戏平台,在线游戏,休闲游戏,益智游戏,体育游戏'
              : 'top mini games,gaming platform,online games,casual games,puzzle games,sports games'
            }
          />
          <meta 
            property="og:title" 
            content={language === 'zh-CN'
              ? '顶尖小游戏 - 海量精彩游戏等你体验'
              : 'Top Mini Games - Enjoy a Variety of Exciting Games'
            }
          />
          <meta 
            property="og:description" 
            content={language === 'zh-CN'
              ? '多合一游戏平台，提供各类休闲、动作、益智、体育游戏，一站式畅玩体验。'
              : 'An all-in-one gaming platform offering various casual, action, puzzle, and sports games for a one-stop gaming experience.'
            }
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`${siteUrl}${pathname}`} />
          <meta property="og:image" content={`${siteUrl}/default-game.jpg`} />
          <html lang={language} />
          
          {/* 多语言SEO优化 - 添加hreflang标签 */}
          {language === 'zh-CN' && (
            <link rel="alternate" hreflang="zh-CN" href={`${siteUrl}/zh${pathname}`} />
          )}
          {language === 'en-US' && (
            <link rel="alternate" hreflang="en-US" href={`${siteUrl}/en${pathname}`} />
          )}
          <link rel="alternate" hreflang="x-default" href={`${siteUrl}${pathname}`} />
        </Helmet>

        <Header />
        
        <main className="flex-grow">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/games/:gameId" element={<GamePage />} />
              <Route path="/games/by-slug/:gameSlug" element={<GamePage />} />
              <Route path="/:categoryName/:gameSlug" element={<GamePage />} />
              <Route path="/category/:categoryName" element={<CategoryPage />} />
              <Route path="/:categoryName" element={<CategoryPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
        
        <Footer />
      </div>
    </LanguageContext.Provider>
  );
}

export default App; 